<template>
    <div
        class="col-span-12 lg:col-span-6 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/50 overflow-hidden p-6 pb-8 relative md:min-h-[400px]"
    >
        <!-- Header -->
        <div
            class="flex items-center justify-between mb-6"
            v-if="walletCardCount > 0"
        >
            <div class="flex items-center gap-2">
                <Icon
                    icon="calendar-check"
                    width="24"
                    height="24"
                    class="text-slate-800 dark:text-slate-100"
                />
                <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
                    Minimum spend deadlines
                </h3>
            </div>

            <!-- View Cards Link (MY1-3047: reveal the Welcome bonus column) -->
            <Link
                :href="`/wallet?show_column=welcome_bonus`"
                class="link-base link-dark"
            >
                View cards
                <icon icon="arrow" />
            </Link>
        </div>

        <!-- Tabs (MY1-3047): only while both groups have — or had — rows.
             An emptied tab keeps an empty state until the page reloads. -->
        <div
            v-if="!loading && walletCardCount > 0 && showTabs"
            class="flex items-center gap-2 mb-4"
        >
            <button
                type="button"
                :class="tabClass('in_progress')"
                @click="selectTab('in_progress')"
            >
                In progress
            </button>
            <button
                type="button"
                :class="tabClass('needs_action')"
                @click="selectTab('needs_action')"
            >
                Needs action{{
                    needsActionCards.length > 0
                        ? ` · ${needsActionCards.length}`
                        : ''
                }}
            </button>
        </div>

        <!-- Empty State: no bonuses at all -->
        <div
            v-if="
                !loading &&
                cards.length === 0 &&
                walletCardCount > 0 &&
                !showTabs
            "
            class="text-center py-12"
        >
            <div
                class="mx-auto w-16 h-16 mb-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center"
            >
                <Icon
                    icon="calendar-check"
                    width="32"
                    height="32"
                    class="w-full h-full text-slate-800 dark:text-slate-100"
                />
            </div>
            <h4 class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-2">
                No minimum spend deadlines yet
            </h4>
            <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center">
                Add a card to your wallet to see upcoming welcome bonus
                deadlines here
            </p>
        </div>

        <!-- Empty State: the active tab was emptied by an action (kept until
             the page reloads, per the design dev note) -->
        <div
            v-if="
                !loading &&
                walletCardCount > 0 &&
                showTabs &&
                displaySourceCards.length === 0
            "
            class="text-center py-12"
        >
            <div
                class="mx-auto w-16 h-16 mb-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center"
            >
                <Icon
                    icon="calendar-check"
                    width="32"
                    height="32"
                    class="w-full h-full text-slate-800 dark:text-slate-100"
                />
            </div>
            <template v-if="activeTab === 'needs_action'">
                <h4
                    class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-2"
                >
                    You're all caught up
                </h4>
                <p
                    class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                >
                    Bonuses with completed goals or passed due dates will
                    appear here
                </p>
            </template>
            <template v-else>
                <h4
                    class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-2"
                >
                    No minimum spend deadlines yet
                </h4>
                <p
                    class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                >
                    Add a card to your wallet to see upcoming welcome bonus
                    deadlines here
                </p>
            </template>
        </div>

        <div
            v-if="!loading && walletCardCount === 0"
            class="w-full flex flex-col items-center pt-4 justify-center"
        >
            <div
                class="w-full flex flex-col text-center items-center justify-center pb-6"
            >
                <img
                    src="../../../../images/min-spend-deadline-promo.png"
                    alt="Minimum spend deadlines"
                />
                <p class="mb-4">
                    <span class="font-bold text-slate-800 dark:text-slate-100 text-3xl">
                        Never miss your
                    </span>
                    <br />
                    <span class="font-bold text-orange text-3xl">
                        welcome bonus
                    </span>
                </p>
                <p class="text-slate-500 dark:text-slate-400 text-lg px-6 mb-6">
                    Add your cards to see spend deadlines
                    <br />
                    and never miss out on rewards.
                </p>
                <Link class="btn btn-orange" href="/wallet">
                    View my wallet
                </Link>
            </div>
        </div>

        <!-- Cards List -->
        <div
            v-if="
                !loading &&
                walletCardCount > 0 &&
                displaySourceCards.length > 0
            "
            class="relative"
            :class="{
                'pb-8': showAll,
                'overflow-hidden max-h-[300px]': !showAll,
            }"
        >
            <div class="space-y-4">
                <div
                    v-for="card in displayedCards"
                    :key="`${card.id}-${card.welcome_bonus?.tier_id ?? 'b'}`"
                    class="pb-4 border-b border-slate-200 dark:border-slate-700 last:border-b-0 last:pb-0"
                >
                <div class="flex items-center gap-4">
                    <div
                        class="flex flex-col lg:flex-row items-start lg:items-center gap-2 md:gap-4 flex-1"
                    >
                        <!-- Card Details -->
                        <div class="order-2 lg:order-1 flex-1">
                            <div class="flex-1 min-w-0">
                                <!-- Bonus Description -->
                                <p
                                    class="text-base font-medium text-slate-800 dark:text-slate-100 mb-1"
                                >
                                    {{ welcomBonusTitle(card) }}
                                </p>

                                <!-- Card Title -->
                                <div
                                    class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400"
                                >
                                    <img
                                        v-if="card.card?.card_image"
                                        :src="
                                            getCardImageUrl(
                                                card.card.card_image
                                            )
                                        "
                                        :alt="card.card.title"
                                        class="w-8 h-5 rounded object-cover shrink-0"
                                    />
                                    <div
                                        v-else
                                        class="w-12 h-8 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center shrink-0"
                                    >
                                        <Icon
                                            icon="credit-card"
                                            class="w-4 h-4 text-slate-400"
                                        />
                                    </div>
                                    {{
                                        card.card?.title ||
                                        `Card ending in ${card.last_four}`
                                    }}
                                    <span v-if="card.last_four">
                                        ({{ card.last_four }})
                                    </span>
                                </div>
                            </div>
                        </div>

                        <span
                            v-tooltip="bonusDueDateTooltip(card.welcome_bonus)"
                            :class="
                                getDeadlineBadgeClass(card) +
                                ' ' +
                                getDeadlineBadgeBgClass(card)
                            "
                            class="inline-block order-1 lg:order-2 rounded px-2 py-0.5 text-sm font-medium whitespace-nowrap"
                        >
                            {{ getDeadlineText(card) }}
                        </span>
                    </div>

                    <!-- Row actions (MY1-3048): in-progress rows keep the
                         single mark-received button; needs-action rows get
                         the state's primary action plus a ⋯ overflow with
                         both, matching card details. The overflow waits for
                         the deadline (MY1-3074) — see showRowOverflow. -->
                    <div class="order-3 flex items-center gap-2">
                        <button
                            v-if="trackingState(card) !== 'deadline_passed'"
                            @click="openMarkReceivedModal(card, $event)"
                            class="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            v-tooltip="'Mark as received'"
                            aria-label="Mark as received"
                        >
                            <Icon
                                icon="check-lg"
                                width="38"
                                height="40"
                                class="text-slate-600 dark:text-slate-400"
                            />
                        </button>
                        <button
                            v-else
                            @click="confirmGoalNotMet(card)"
                            class="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            v-tooltip="'Goal wasn\'t met, bonus missed'"
                            aria-label="Goal wasn't met, bonus missed"
                        >
                            <Icon
                                icon="circle-x"
                                width="22"
                                height="22"
                                class="text-slate-600 dark:text-slate-400"
                            />
                        </button>

                        <div
                            v-if="showRowOverflow(card)"
                            class="relative"
                        >
                            <button
                                @click.stop="toggleRowMenu(card)"
                                class="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                aria-label="More bonus actions"
                            >
                                <Icon
                                    icon="dots-vertical"
                                    width="20"
                                    height="20"
                                    class="text-slate-600 dark:text-slate-400"
                                />
                            </button>
                            <div
                                v-if="openRowMenuKey === rowKey(card)"
                                class="absolute right-0 top-full mt-2 flex p-2 flex-col items-start border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 z-30 min-w-[250px]"
                                @click.stop
                            >
                                <button
                                    class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm font-normal text-slate-900 dark:text-slate-100 whitespace-nowrap"
                                    @click="onRowMenuMark(card, $event)"
                                >
                                    <Icon icon="check-lg" class="w-4 h-4" />
                                    Goal reached, bonus received
                                </button>
                                <button
                                    class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm font-normal text-slate-900 dark:text-slate-100 whitespace-nowrap"
                                    @click="onRowMenuGoalNotMet(card)"
                                >
                                    <Icon icon="circle-x" class="w-4 h-4" />
                                    Goal wasn't met, bonus missed
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Spend progress (MY1-3047): same state logic as card
                     details; hidden states render no bar at all. Hover shows
                     the amounts (MY1-3049). -->
                <div v-tooltip="bonusBarTooltip(card.welcome_bonus)">
                    <BonusProgressBar
                        :bonus="card.welcome_bonus"
                        label-placement="hidden"
                        class="mt-3"
                    />
                </div>
                </div>
            </div>
        </div>

        <div
            v-if="hasMoreCards && !showAll"
            class="absolute bottom-12 left-0 right-0 h-24 bg-gradient-to-t from-white dark:from-slate-800 via-white/80 dark:via-slate-800/80 to-transparent pointer-events-none"
        ></div>

        <!-- Show More/Less Link -->
        <div
            v-if="hasMoreCards"
            class="py-1 pb-2 absolute bottom-4 left-6 right-4 bg-white dark:bg-slate-800 z-10"
        >
            <button
                @click="showAll = !showAll"
                class="font-medium text-sm text-slate-800 dark:text-slate-100 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
                {{ showAll ? 'Show less' : 'Show all' }}
            </button>
        </div>

        <!-- Mark Received Popover -->
        <teleport to="body">
            <div
                v-if="showMarkReceivedModal"
                class="fixed inset-0 z-50"
                @click.self="closeMarkReceivedModal"
            >
                <div
                    ref="popoverElement"
                    class="absolute bg-white dark:bg-slate-800 rounded-lg border flex flex-col items-end border-slate-200 dark:border-slate-700 shadow dark:shadow-slate-900/50 w-80 p-4"
                    :style="popoverStyle"
                >
                    <div class="mb-4 w-full">
                        <UiInput
                            id="date-received"
                            name="date_received"
                            label="Date bonus received"
                            placeholder="Date bonus received"
                            :options="{
                                icon: 'calendar',
                            }"
                        >
                            <template v-slot:input>
                                <FlatPickr
                                    :config="dateConfig"
                                    name="date_received"
                                    :modelValue="dateReceived"
                                    id="date-received"
                                    class="relative block w-full rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] overflow-hidden bg-white dark:bg-slate-800 py-1.5 pl-10 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                    placeholder="Date bonus received"
                                    @on-change="
                                        (dates, v) => dateReceivedChange(dates)
                                    "
                                />
                            </template>
                        </UiInput>
                        <p
                            v-if="validationError"
                            class="mt-2 text-sm text-red-600"
                        >
                            {{ validationError }}
                        </p>
                    </div>

                    <button
                        @click="markBonusReceived"
                        :disabled="submitting"
                        class="btn btn-primary !w-40 flex items-center gap-2"
                    >
                        <Icon icon="check-lg" />
                        {{ submitting ? 'Saving...' : 'Mark received' }}
                    </button>
                </div>
            </div>
        </teleport>

        <Sheet
            :open="sheetOpen"
            title="Mark bonus as received"
            size="xs"
            @update:open="sheetOpen = $event"
        >
            <div class="w-full mb-6">
                <UiInput
                    id="date-received-2"
                    name="date_received_2"
                    label="Date bonus received"
                    placeholder="Date bonus received"
                    :options="{
                        icon: 'calendar',
                    }"
                >
                    <template v-slot:input>
                        <FlatPickr
                            :config="dateConfig"
                            name="date_received_2"
                            :modelValue="dateReceived"
                            id="date-received-2"
                            class="relative block w-full rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] overflow-hidden bg-white dark:bg-slate-800 py-1.5 pl-10 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            placeholder="Date bonus received"
                            @on-change="(dates, v) => dateReceivedChange(dates)"
                        />
                    </template>
                </UiInput>
                <p v-if="validationError" class="mt-2 text-sm text-red-600">
                    {{ validationError }}
                </p>
            </div>

            <div class="flex justify-between gap-2">
                <button
                    @click="sheetOpen = false"
                    class="btn btn-secondary !w-40"
                >
                    Cancel
                </button>

                <button
                    @click="markBonusReceived"
                    :disabled="submitting"
                    class="btn btn-primary flex items-center gap-2 !w-auto"
                >
                    <Icon icon="check-lg" />
                    {{ submitting ? 'Saving...' : 'Mark received' }}
                </button>
            </div>
        </Sheet>
    </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import { Apii } from '@/services/api.js';
import { notify } from 'notiwind';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Sheet from '@/components/ui/sheet/sheet.vue';
import BonusProgressBar from '@/components/wallet/partials/bonus-progress-bar.vue';
import {
    bonusBarTooltip,
    bonusDeadlinePassed,
    bonusDueDateTooltip,
} from '@/composables/useBonusTracking';
import dayjs from 'dayjs';
import Filter from '@/filters';

const ApiService = new Apii();

const dashboardStore = useDashboardStore();
const cdnUrl = ref('');
const showMarkReceivedModal = ref(false);
const selectedCardForModal = ref(null);
const dateReceived = ref('');
const validationError = ref('');
const submitting = ref(false);
const popoverElement = ref(null);
const popoverStyle = ref({});
const triggerElement = ref(null);
const showAll = ref(false);
const INITIAL_DISPLAY_COUNT = 4;
const sheetOpen = ref(false);
const dateRecDate = ref('');

let viewWidth = window.innerWidth;
let dateConfig = {
    dateFormat: 'm/d/Y',
    maxDate: 'today',
    minDate: null,
    disableMobile: 'true',
};

const currentUser = computed(() => dashboardStore.user);
const cards = computed(() => dashboardStore.minSpendDeadlines || []);

// MY1-3047 — tab grouping off the MY1-3037 tracking state. "Needs action"
// holds bonuses whose goal was hit or whose deadline passed; everything else
// is still in progress.
const NEEDS_ACTION_STATES = ['goal_reached', 'deadline_passed'];

const trackingState = (card) => card.welcome_bonus?.tracking?.state ?? null;

const needsActionCards = computed(() =>
    cards.value.filter((card) =>
        NEEDS_ACTION_STATES.includes(trackingState(card))
    )
);
const inProgressCards = computed(() =>
    cards.value.filter(
        (card) => !NEEDS_ACTION_STATES.includes(trackingState(card))
    )
);

// Tabs show only while both groups have rows — but an emptied tab keeps its
// empty state until the page reloads (dev note), so the flags are sticky.
const hadInProgress = ref(false);
const hadNeedsAction = ref(false);
watch(
    inProgressCards,
    (rows) => {
        if (rows.length > 0) hadInProgress.value = true;
    },
    { immediate: true }
);
watch(
    needsActionCards,
    (rows) => {
        if (rows.length > 0) hadNeedsAction.value = true;
    },
    { immediate: true }
);

const showTabs = computed(() => hadInProgress.value && hadNeedsAction.value);
const activeTab = ref('in_progress');

// If the widget arrives with only needs-action rows, land on that tab once
// tabs materialize.
watch(showTabs, (visible) => {
    if (
        visible &&
        inProgressCards.value.length === 0 &&
        needsActionCards.value.length > 0
    ) {
        activeTab.value = 'needs_action';
    }
});

const selectTab = (tab) => {
    activeTab.value = tab;
    showAll.value = false;
};

// MY1-3048 — per-row actions. A tier pseudo-row shares the account id with
// its siblings, so rows are keyed by account + tier.
const rowKey = (card) => `${card.id}-${card.welcome_bonus?.tier_id ?? 'b'}`;

const isNeedsActionRow = (card) =>
    NEEDS_ACTION_STATES.includes(trackingState(card));

// MY1-3074 — resolveState() returns goal_reached as soon as spend clears the
// minimum, which can be months before the deadline, while the endpoint refuses
// goal_not_met until the deadline is strictly past. The shared (UTC) predicate
// keeps this row in step with the wallet strip and with the API.
//
// On such a row the overflow's only unique offer is that missed-goal
// confirmation — the other item repeats the primary Mark received button — so
// the whole ⋯ menu waits for the deadline with it. A deadline_passed row is
// past its deadline by definition, so its overflow is unaffected.
const showRowOverflow = (card) =>
    isNeedsActionRow(card) && bonusDeadlinePassed(card.welcome_bonus);

const openRowMenuKey = ref(null);

const toggleRowMenu = (card) => {
    openRowMenuKey.value =
        openRowMenuKey.value === rowKey(card) ? null : rowKey(card);
};

const onRowMenuMark = (card, event) => {
    openRowMenuKey.value = null;
    openMarkReceivedModal(card, event);
};

const onRowMenuGoalNotMet = (card) => {
    openRowMenuKey.value = null;
    confirmGoalNotMet(card);
};

const closeRowMenus = () => {
    openRowMenuKey.value = null;
};

const confirmGoalNotMet = async (card) => {
    try {
        await ApiService.apiCall(
            `/wallets/${currentUser.value.wallet_id}/users/${currentUser.value.uuid}/welcome-bonus/${card.welcome_bonus.id}`,
            'put',
            {
                goal_not_met: true,
                // SUPPORT-745: tier rows must dismiss the tier, not the parent.
                tier_id: card.welcome_bonus.tier_id ?? null,
            }
        );

        notify(
            {
                group: 'success',
                title: 'Success',
                text: 'Bonus tracking dismissed',
            },
            4000
        );

        dashboardStore.minSpendDeadlines =
            dashboardStore.minSpendDeadlines.filter(
                (row) => rowKey(row) !== rowKey(card)
            );
        dashboardStore.getMinSpendDeadlines();
    } catch (error) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Failed to update the welcome bonus',
            },
            4000
        );
    }
};

const tabClass = (tab) => {
    const base =
        'rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap';
    if (activeTab.value === tab) {
        return `${base} bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-800`;
    }
    return `${base} bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700`;
};

// Days until deadline: prefer the API's tracking value, fall back to a local
// diff for rows without a tracking object (stale cache).
const daysFor = (card) => {
    const days = card.welcome_bonus?.tracking?.days_remaining;
    if (days !== null && days !== undefined) {
        return Number(days);
    }
    return getDaysRemaining(card);
};

// The active tab's rows, soonest deadline first.
const displaySourceCards = computed(() => {
    const source = showTabs.value
        ? activeTab.value === 'needs_action'
            ? needsActionCards.value
            : inProgressCards.value
        : cards.value;

    return [...source].sort((a, b) => daysFor(a) - daysFor(b));
});

const displayedCards = computed(() => {
    if (
        showAll.value ||
        displaySourceCards.value.length <= INITIAL_DISPLAY_COUNT
    ) {
        return displaySourceCards.value;
    }
    return displaySourceCards.value.slice(0, INITIAL_DISPLAY_COUNT);
});
const hasMoreCards = computed(() => {
    if (viewWidth <= 768) {
        return displaySourceCards.value.length > 2;
    }
    if (viewWidth > 768) {
        return displaySourceCards.value.length > INITIAL_DISPLAY_COUNT;
    }
    return false;
});
const loading = computed(() => dashboardStore.loading);
const { walletCardCount } = storeToRefs(dashboardStore);

const freeNightTypes = {
    hilton: 'Hilton free nights',
    hyatt: 'Hyatt free nights',
    ihg: 'IHG free nights',
    marriott: 'Marriott free nights',
};

const getCardImageUrl = (cardImage) => {
    if (cardImage?.file_name) {
        return `${cdnUrl.value}/${cardImage.path}/_small/${cardImage.file_name}`;
    }
    return '/images/cards/default.png';
};

const getDaysRemaining = (card) => {
    if (!card.welcome_bonus?.minimum_spend_deadline) return 0;
    const deadline = dayjs(card.welcome_bonus.minimum_spend_deadline);
    const now = dayjs();
    return deadline.diff(now, 'day');
};

// MY1-3047 — badge copy: "Due reached" once the deadline passed, the money
// form ("$500 in 2 days") while actively tracking with an amount left, and
// the plain day count otherwise.
const getDeadlineText = (card) => {
    const state = trackingState(card);
    const days = daysFor(card);

    if (card.welcome_bonus?.received) return 'Completed';
    if (state === 'deadline_passed' || days < 0) return 'Due reached';

    const dayWord = days === 1 ? 'day' : 'days';
    const remaining = Number(
        card.welcome_bonus?.tracking?.amount_remaining ?? 0
    );

    if (state === 'tracking' && remaining > 0) {
        return `$${Filter.formatThousand(remaining)} in ${days} ${dayWord}`;
    }

    return `${days} ${dayWord}`;
};

// Red at ≤30 days, only while the goal is still unmet and reachable.
const isDeadlineUrgent = (card) => {
    const state = trackingState(card);
    const days = daysFor(card);

    return (
        days >= 0 &&
        days <= 30 &&
        state !== 'goal_reached' &&
        state !== 'deadline_passed' &&
        !card.welcome_bonus?.received
    );
};

const getDeadlineBadgeClass = (card) => {
    if (isDeadlineUrgent(card)) return 'text-red-600';
    return 'text-slate-500 dark:text-slate-400';
};

const getDeadlineBadgeBgClass = (card) => {
    if (isDeadlineUrgent(card)) return 'bg-red-100';
    return 'bg-slate-100 dark:bg-slate-700';
};

const dateReceivedChange = (v) => {
    dateRecDate.value = v[0];
};

const openMarkReceivedModal = (card, event) => {
    selectedCardForModal.value = card;
    if (card.date_opened) {
        dateConfig.minDate = dayjs(card.date_opened).format('MM/DD/YYYY');
    }
    dateReceived.value = dayjs().format('MM/DD/YYYY');
    dateRecDate.value = dayjs().format('MM/DD/YYYY');
    validationError.value = '';

    if (viewWidth < 769) {
        sheetOpen.value = true;
    } else {
        showMarkReceivedModal.value = true;
        triggerElement.value = event.currentTarget;
        nextTick(() => {
            if (popoverElement.value && triggerElement.value) {
                const triggerRect =
                    triggerElement.value.getBoundingClientRect();
                const popoverRect =
                    popoverElement.value.getBoundingClientRect();

                // Position below and aligned to the right of the trigger
                const top = triggerRect.bottom + 8;
                const left = triggerRect.right - popoverRect.width;

                popoverStyle.value = {
                    top: `${top}px`,
                    left: `${left}px`,
                };
            }
        });
    }
};

const welcomBonusTitle = (card) => {
    if (card.welcome_bonus?.type === 'points') {
        return `Spend $${Filter.formatThousand(card.welcome_bonus?.minimum_spend_amount)} to earn ${Filter.formatThousand(card.welcome_bonus?.bonus_amount)} bonus points`;
    }

    if (card.welcome_bonus?.type === 'cashback') {
        return `Spend $${Filter.formatThousand(card.welcome_bonus?.minimum_spend_amount)} to earn $${Filter.formatThousand(card.welcome_bonus?.bonus_amount)} cash back`;
    }

    if (card.welcome_bonus?.type === 'free_nights') {
        return `Spend $${Filter.formatThousand(card.welcome_bonus?.minimum_spend_amount)} to earn ${Filter.formatThousand(card.welcome_bonus?.bonus_amount)} free nights in ${freeNightTypes[card.welcome_bonus?.bonus_units]}`;
    }

    return card.welcome_bonus?.bonus_description || 'Welcome bonus';
};

const closeMarkReceivedModal = () => {
    sheetOpen.value = false;
    showMarkReceivedModal.value = false;
    selectedCardForModal.value = null;
    dateReceived.value = '';
    dateRecDate.value = '';
    validationError.value = '';
};

const markBonusReceived = async () => {
    if (!dateRecDate.value) {
        validationError.value = 'Please select a date';
        return;
    }

    if (dayjs(dateRecDate.value).isAfter(dayjs())) {
        validationError.value = 'Date cannot be in the future';
        return;
    }

    submitting.value = true;
    validationError.value = '';

    try {
        await ApiService.apiCall(
            // SUPPORT-762: the dashboard's household wallet id (set once from
            // userData.wallet_id) is the wallet that owns the card and is the
            // wallet the caller belongs to. `currentUser.wallet_id` is the
            // selected pill user's OWN primary wallet, which for a P2 attached
            // to a shared household points at a wallet the caller is not a
            // member of — the request 403s at WelcomeBonusUpdateRequest. Use
            // the same wallet id the GET already uses so both hit the same
            // wallet-scoped authorization contract.
            `/wallets/${dashboardStore.walletId}/users/${currentUser.value.uuid}/welcome-bonus/${selectedCardForModal.value.welcome_bonus.id}`,
            'put',
            {
                received: true,
                date_received: dateRecDate.value,
                // SUPPORT-745: for a tiered bonus the parent id above scopes the
                // lookup, but the received state must land on this specific tier.
                tier_id: selectedCardForModal.value.welcome_bonus.tier_id ?? null,
            }
        );

        notify(
            {
                group: 'success',
                title: 'Success',
                text: 'Bonus marked as received',
            },
            4000
        );

        // Remove the row from the list in the store. Keyed by account + tier:
        // a tiered bonus lists one row per unreceived tier under the same
        // account id, and marking one must not drop its siblings (MY1-3048).
        dashboardStore.minSpendDeadlines =
            dashboardStore.minSpendDeadlines.filter(
                (card) => rowKey(card) !== rowKey(selectedCardForModal.value)
            );

        dashboardStore.getMinSpendDeadlines();

        closeMarkReceivedModal();
    } catch (error) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Failed to mark bonus as received',
            },
            4000
        );
    } finally {
        submitting.value = false;
    }
};

const updateViewWidth = () => {
    viewWidth = window.innerWidth;
};

onMounted(() => {
    cdnUrl.value = window.cdnUrl;
    updateViewWidth();
    window.addEventListener('resize', updateViewWidth);
    document.addEventListener('click', closeRowMenus);
});
onUnmounted(() => {
    viewWidth = null;
    window.removeEventListener('resize', updateViewWidth);
    document.removeEventListener('click', closeRowMenus);
});
</script>
